import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CabinetHeader } from "../components/CabinetHeader";
import { Button } from "../components/Button";
import { ErrorBanner } from "../components/ErrorBanner";
import { getPost, deletePost } from "../api/posts";
import { ModerationStatus, PostType, ExternalUrlKind } from "../types/gordost";

const POST_TYPE_LABELS: Record<PostType, string> = {
  diploma: "🏆 Диплом",
  project: "🎯 Проект",
  initiative: "✨ Инициатива",
  gratitude: "💝 Благодарность",
  sport: "⚽ Спорт",
};

const STATUS_LABELS: Record<ModerationStatus, string> = {
  pending: "⏳ На проверке у учителя",
  approved: "✓ Опубликовано",
  rejected: "✕ Отклонено учителем",
  needs_revision: "✏️ Учитель просит правки",
};

const STATUS_STYLES: Record<ModerationStatus, string> = {
  pending: "bg-[#f4f4f5] text-[var(--color-muted)]",
  approved: "bg-[#eef1ff] text-[var(--color-cobalt)]",
  rejected: "bg-[#fff5f4] text-[var(--color-red)]",
  needs_revision: "bg-[#fff5f4] text-[var(--color-red)]",
};

function embedUrl(url: string, kind: ExternalUrlKind | null): string | null {
  if (!kind || kind === "other") return null;
  try {
    if (kind === "rutube") {
      const m = url.match(/rutube\.ru\/video\/([\w-]+)/);
      if (m) return `https://rutube.ru/play/embed/${m[1]}`;
    }
    if (kind === "youtube") {
      let id: string | null = null;
      const m1 = url.match(/youtube\.com\/watch\?v=([\w-]+)/);
      const m2 = url.match(/youtu\.be\/([\w-]+)/);
      if (m1) id = m1[1];
      else if (m2) id = m2[1];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (kind === "vk") {
      // VK embed format: vk.com/video_ext.php?oid=...&id=...
      const m = url.match(/video(-?\d+)_(\d+)/);
      if (m) {
        return `https://vk.com/video_ext.php?oid=${m[1]}&id=${m[2]}&hd=2`;
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function PostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const postId = parseInt(id || "0", 10);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const { data: post, isLoading, error: queryError } = useQuery({
    queryKey: ["post", postId],
    queryFn: () => getPost(postId),
    enabled: postId > 0,
  });

  async function onDelete() {
    if (!post) return;
    if (
      !window.confirm(
        `Удалить публикацию «${post.title}»? Действие необратимо.`
      )
    ) {
      return;
    }
    setError("");
    setDeleting(true);
    try {
      await deletePost(post.id);
      await queryClient.invalidateQueries({ queryKey: ["posts"] });
      navigate("/cabinet");
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Не удалось удалить");
      setDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)] flex items-center justify-center">
        <p className="text-[var(--color-muted)]">Загрузка…</p>
      </div>
    );
  }
  if (queryError || !post) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)]">
        <CabinetHeader />
        <main className="max-w-3xl mx-auto px-6 py-8 text-center">
          <p className="text-[var(--color-accent)] mb-4">Публикация не найдена</p>
          <Link to="/cabinet" className="text-[var(--color-accent)] underline">
            ← В кабинет
          </Link>
        </main>
      </div>
    );
  }

  const canEdit =
    post.moderation_status === "pending" ||
    post.moderation_status === "needs_revision";
  const canDelete = post.moderation_status !== "approved";
  const videoEmbed = post.external_url
    ? embedUrl(post.external_url, post.external_url_kind)
    : null;

  return (
    <div className="min-h-screen bg-[var(--color-paper)]">
      <CabinetHeader />
      <main className="max-w-3xl mx-auto px-6 py-8">
        <Link
          to="/cabinet"
          className="text-sm text-[var(--color-muted)] hover:text-[var(--color-accent)] mb-4 inline-block"
        >
          ← В кабинет
        </Link>

        <ErrorBanner message={error} />

        <article className="bg-white border border-[var(--color-line)] p-6 mb-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[var(--color-muted)] mb-1">
                {POST_TYPE_LABELS[post.post_type]}
                {post.event_date && (
                  <>
                    {" · "}
                    {new Date(post.event_date).toLocaleDateString("ru-RU")}
                  </>
                )}
              </div>
              <h1 className="text-3xl mb-2">{post.title}</h1>
            </div>
            <span
              className={`px-3 py-1 text-xs font-bold whitespace-nowrap ${STATUS_STYLES[post.moderation_status]}`}
            >
              {STATUS_LABELS[post.moderation_status]}
            </span>
          </div>

          {/* Метка признания (A1.1): подтверждённая модерация как формат
              институционального признания, а не просто статус. */}
          {post.moderation_status === "approved" && (
            <div className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-[var(--color-cobalt)] bg-[#eef1ff] px-3 py-2">
              <span aria-hidden>✓</span>
              <span>
                {post.confirmed_level === "admin"
                  ? "Подтверждено администрацией"
                  : post.confirmed_level === "classroom"
                    ? "Подтверждено классным руководителем"
                    : "Подтверждено школой"}
              </span>
            </div>
          )}

          {/* Moderator note */}
          {post.moderation_comment &&
            (post.moderation_status === "needs_revision" ||
              post.moderation_status === "rejected") && (
              <div
                className={`mb-4 p-3 border-l-4 ${
                  post.moderation_status === "rejected"
                    ? "bg-red-50 border-red-400"
                    : "bg-yellow-50 border-yellow-400"
                }`}
              >
                <div className="text-xs font-bold mb-1">
                  Комментарий учителя:
                </div>
                <div className="text-sm whitespace-pre-wrap">
                  {post.moderation_comment}
                </div>
              </div>
            )}

          {/* Body */}
          <div className="whitespace-pre-wrap leading-relaxed mb-6">
            {post.body}
          </div>

          {/* Files */}
          {post.files.length > 0 && (
            <div className="mb-6">
              <h2
                className="text-lg mb-3"
                style={{ fontFamily: "var(--font-serif)" }}
              >
                Файлы
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {post.files.map((f) => (
                  <div
                    key={f.id}
                    className="border border-[var(--color-line)] p-2 bg-[var(--color-paper)]"
                  >
                    {f.mime_type.startsWith("image/") && f.download_url ? (
                      <a href={f.download_url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={f.download_url}
                          alt={f.original_filename}
                          className="w-full h-48 object-cover"
                          loading="lazy"
                        />
                      </a>
                    ) : (
                      <div className="h-48 flex items-center justify-center bg-white text-[var(--color-muted)]">
                        📄 PDF
                      </div>
                    )}
                    <div className="mt-2 text-xs text-[var(--color-muted)] truncate">
                      {f.original_filename}
                    </div>
                    {f.download_url && (
                      <a
                        href={f.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[var(--color-accent)] underline"
                        download={f.original_filename}
                      >
                        Скачать
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* External video */}
          {post.external_url && (
            <div className="mb-6">
              <h2
                className="text-lg mb-3"
                style={{ fontFamily: "var(--font-serif)" }}
              >
                Видео
              </h2>
              {videoEmbed ? (
                <div className="aspect-video w-full bg-black">
                  <iframe
                    src={videoEmbed}
                    title={post.title}
                    className="w-full h-full"
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <a
                  href={post.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--color-accent)] underline break-all"
                >
                  {post.external_url}
                </a>
              )}
            </div>
          )}

          {/* Meta */}
          <div className="pt-4 border-t border-[var(--color-line)] text-xs text-[var(--color-muted)]">
            Создано:{" "}
            {new Date(post.created_at).toLocaleString("ru-RU")}
            {post.updated_at &&
              ` · Изменено: ${new Date(post.updated_at).toLocaleString("ru-RU")}`}
          </div>
        </article>

        {/* Actions */}
        <div className="flex gap-3 flex-wrap">
          {canEdit && (
            <Link to={`/cabinet/post/${post.id}/edit`}>
              <Button variant="primary">Редактировать</Button>
            </Link>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              onClick={onDelete}
              loading={deleting}
              className="text-[var(--color-accent)] border-[var(--color-accent)]"
            >
              Удалить
            </Button>
          )}
          {!canEdit && !canDelete && (
            <p className="text-sm text-[var(--color-muted)] italic">
              Опубликованную запись изменить нельзя. Чтобы внести правку —
              обратитесь к классному руководителю.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
